import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip } from 'antd';
import styles from './BindMatrix.module.css';
import Spinner from '../Spinner/Spinner';

const ROW_BIND_ALL = 'bindall';

function BindAllCheckbox({ checked, indeterminate, onClick }) {
	const ref = useRef(null);
	useEffect(() => {
		if (ref.current) {
			ref.current.indeterminate = indeterminate;
		}
	}, [indeterminate]);

	return (
		<input
			ref={ref}
			type="checkbox"
			checked={checked}
			onChange={onClick}
			className={styles.checkbox}
		/>
	);
}

/**
 * Universal bind matrix.
 * @param {Array<{id: number, label: string}>} rows
 * @param {Array<{id: number|string, label: string}>} columns
 * @param {Set<string>} currentState  - set of "${rowId}-${colId}" keys
 * @param {Set<string>} baseState     - original saved state
 * @param {function(rowId, colId)} toggleCell
 * @param {function(colId)} toggleAll
 * @param {boolean} loading
 */
export default function BindMatrix({ rows, columns, currentState, baseState, toggleCell, toggleAll, loading }) {
	const [hoverRow, setHoverRow] = useState(null);
	const [hoverCol, setHoverCol] = useState(null);

	const checkedCountByCol = useMemo(() => {
		const map = {};
		columns.forEach(col => { map[col.id] = 0; });
		currentState.forEach(key => {
			const dash = key.indexOf('-');
			const colId = key.slice(dash + 1);
			if (colId in map) map[colId]++;
		});
		return map;
	}, [columns, currentState]);

	const clearHover = () => {
		setHoverRow(null);
		setHoverCol(null);
	};

	const cellClasses = (rowId, colId, isChecked, isChanged) => {
		let cls = styles.cell;
		if (isChanged) {
			cls += isChecked ? ` ${styles.cellAdded}` : ` ${styles.cellRemoved}`;
		}
		if (hoverRow === rowId) cls += ` ${styles.hiliteRow}`;
		if (hoverCol === colId) cls += ` ${styles.hiliteCol}`;
		return cls;
	};

	return (
		<div className={styles.matrixOuter}>
			{loading && (
				<div className={styles.matrixOverlay}>
					<Spinner />
				</div>
			)}
			<div
				className={styles.matrixWrapper}
				onMouseLeave={clearHover}
			>
				<table className={styles.matrix}>
					<thead>
						<tr>
							<th
								className={styles.cornerCell}
								onMouseEnter={clearHover}
							/>
							{columns.map(col => (
								<th
									key={col.id}
									className={`${styles.headerCell} ${hoverCol === col.id ? styles.headerColHilite : ''}`}
									onMouseEnter={() => {
										setHoverRow(null);
										setHoverCol(col.id);
									}}
								>
									<Tooltip
										title={col.label}
										open={hoverCol === col.id}
										placement="top"
										trigger={[]}
										mouseEnterDelay={0}
										mouseLeaveDelay={0}
										zIndex={10050}
										destroyTooltipOnHide={false}
										classNames={{ root: styles.columnTitleTooltip }}
										styles={{
											body: {
												maxWidth: 440,
												background: 'var(--color_primary_default)',
												color: 'var(--color_primary_contrast_default)',
												fontSize: 13,
												lineHeight: 1.45,
												padding: '8px 12px',
												borderRadius: 6,
												boxShadow: '0 2px 10px rgba(var(--color_primary_default_rgb), 0.35)'
											}
										}}
									>
										<div className={styles.headerText}>
											{col.label}
										</div>
									</Tooltip>
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						<tr className={styles.bindAllRow}>
							<td
								className={`${styles.rowHeader} ${styles.bindAllHeader} ${hoverRow === ROW_BIND_ALL ? styles.rowHeaderHilite : ''}`}
								onMouseEnter={() => {
									setHoverRow(ROW_BIND_ALL);
									setHoverCol(null);
								}}
							>
								Привязать ко всем
							</td>
							{columns.map(col => {
								const checkedCount = checkedCountByCol[col.id] ?? 0;
								const allChecked = checkedCount === rows.length;
								const someChecked = checkedCount > 0 && !allChecked;
								return (
									<td
										key={col.id}
										className={`${styles.cell} ${hoverRow === ROW_BIND_ALL ? styles.hiliteRow : ''} ${hoverCol === col.id ? styles.hiliteCol : ''}`}
										onClick={() => toggleAll(col.id)}
										onMouseEnter={() => {
											setHoverRow(ROW_BIND_ALL);
											setHoverCol(col.id);
										}}
									>
										<BindAllCheckbox
											checked={allChecked}
											indeterminate={someChecked}
											onClick={() => toggleAll(col.id)}
										/>
									</td>
								);
							})}
						</tr>
						{rows.map(row => (
							<tr key={row.id}>
								<td
									className={`${styles.rowHeader} ${hoverRow === row.id ? styles.rowHeaderHilite : ''}`}
									onMouseEnter={() => {
										setHoverRow(row.id);
										setHoverCol(null);
									}}
								>
									<span className={styles.rowHeaderText} title={row.label}>{row.label}</span>
								</td>
								{columns.map(col => {
									const key = `${row.id}-${col.id}`;
									const isChecked = currentState.has(key);
									const wasChecked = baseState.has(key);
									const isChanged = isChecked !== wasChecked;
									return (
										<td
											key={col.id}
											className={cellClasses(row.id, col.id, isChecked, isChanged)}
											onClick={() => toggleCell(row.id, col.id)}
											onMouseEnter={() => {
												setHoverRow(row.id);
												setHoverCol(col.id);
											}}
										>
											<input
												type="checkbox"
												checked={isChecked}
												readOnly
												className={styles.checkbox}
											/>
										</td>
									);
								})}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}
